import {
  DEFAULT_NOTIFICATION_CONFIG,
  NOTIFICATION_CONFIG,
} from '@/shared/constants/notificationConfig.const';
import {
  Button,
  Card,
  CardBody,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Spinner,
} from '@heroui/react';
import { createElement } from 'react';
import { useNavigate } from 'react-router-dom';
import { useNotificationsData } from '@/features/notifications/hooks/useNotificationsData.hook';

interface NotificationsHistoryModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const NotificationsHistoryModal = ({
  isOpen,
  onClose,
}: NotificationsHistoryModalProps) => {
  const navigate = useNavigate();
  const { notifications, loading, error, totalNotifications } =
    useNotificationsData();

  // Convertir las notificaciones del store a un array de tipos activos
  const pendientes = notifications
    ? Object.entries(notifications)
        .filter(([_, isActive]) => isActive)
        .map(([tipo, _]) => {
          // Mapear los nombres del API a los tipos de la configuración
          const tipoMapeado = tipo.replace('riesgo_', '');
          return tipoMapeado;
        })
    : [];

  const handleViewDetails = (tipo: string) => {
    const config = NOTIFICATION_CONFIG[tipo] || DEFAULT_NOTIFICATION_CONFIG;
    onClose();
    navigate(config.actionUrl);
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="2xl"
      scrollBehavior="inside"
      backdrop="blur"
    >
      <ModalContent>
        <ModalHeader className="flex gap-2 items-center border-b border-divider">
          <span className="text-xl">🔔 Notificaciones</span>
          {totalNotifications > 0 && (
            <span className="bg-danger text-white text-xs font-medium px-2 py-1 rounded-full">
              {totalNotifications}
            </span>
          )}
        </ModalHeader>

        <ModalBody className="py-6">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Spinner size="lg" />
              <span className="ml-3 text-default-500">
                Cargando notificaciones...
              </span>
            </div>
          ) : error ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <div className="text-6xl mb-4">⚠️</div>
              <p className="text-lg font-medium text-danger">
                Error al cargar notificaciones
              </p>
              <p className="text-sm text-default-400 mt-2">{error}</p>
            </div>
          ) : pendientes.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <div className="text-6xl mb-4">🎉</div>
              <p className="text-lg font-medium text-default-600">
                No tienes notificaciones pendientes
              </p>
              <p className="text-sm text-default-400 mt-2">
                Aquí aparecerán tus alertas de riesgo académico
              </p>
            </div>
          ) : (
            <div className="flex flex-col gap-3">
              {pendientes.map((tipo, index) => {
                const config =
                  NOTIFICATION_CONFIG[tipo] || DEFAULT_NOTIFICATION_CONFIG;
                const Icon = config.icon;

                return (
                  <Card
                    key={`${tipo}-${index}`}
                    className={`border-l-4 ${config.colorBorder}`}
                    shadow="sm"
                  >
                    <CardBody className="p-4">
                      <div className="flex items-start gap-3">
                        <div className={`mt-1 ${config.colorText}`}>
                          {createElement(Icon, { size: 24 })}
                        </div>

                        <div className="flex-1">
                          <h4 className="font-semibold text-default-900 mb-1">
                            {config.message}
                          </h4>
                          <p className="text-sm text-default-600">
                            Revisa los estudiantes con riesgo en {tipo}
                          </p>
                        </div>

                        <Button
                          color="primary"
                          size="sm"
                          variant="flat"
                          onPress={() => handleViewDetails(tipo)}
                        >
                          Ver Detalles
                        </Button>
                      </div>
                    </CardBody>
                  </Card>
                );
              })}
            </div>
          )}
        </ModalBody>

        <ModalFooter className="border-t border-divider">
          <Button color="default" variant="light" onPress={onClose}>
            Cerrar
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default NotificationsHistoryModal;
